import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DocumentArrowDownIcon, ChevronLeftIcon, ClockIcon, DocumentTextIcon, LinkIcon } from "@heroicons/react/24/outline"
import axiosInstance from '../../api/axiosInstance'
import Departamentos from '../../assets/data/departamentos.json'
import Ciudades from '../../assets/data/ciudades.json'
import { tiposDeDocumento, formaPago, metodoPago } from '../../assets/data/arrays'
import CotizacionForm from '../forms/CotizacionForm'
import toast from "react-hot-toast"
import { generarPDFCotizacion } from '../pdf-templates/CotizacionPDF'
import { Button } from '../ui'

const CotizacionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [cotizacionDetail, setCotizacionDetail] = useState<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false) // Reservado para uso futuro
  const [error, setError] = useState<string | null>(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const navigate = useNavigate()

  const fetchCotizacionDetail = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/cotizacion/${id}`);
      setCotizacionDetail(response.data);
    } catch (error) {
      console.error('Error obteniendo el detalle de la cotización:', error);
      setError('Error al cargar los datos de la cotización');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCotizacion = async () => {
    if (!id) return
    
    try {
      setIsGeneratingPDF(true)
      toast.loading('Generando PDF de cotización...', { position: 'top-center' })
      await generarPDFCotizacion(Number(id))
      toast.dismiss()
      toast.success('Cotización descargada en PDF', { position: 'top-center' })
    } catch (error) {
      toast.dismiss()
      toast.error('Error al generar el PDF de la cotización', { position: 'top-center' })
      console.error(error)
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  useEffect(() => {
    fetchCotizacionDetail();
    // eslint-disable-next-line
  }, [id]);

  function getNumeroCotizacion(cotizacionDetail: any) {
    return (
      cotizacionDetail.cotizaciones_id ||
      cotizacionDetail.cotizacion_id ||
      cotizacionDetail.id ||
      (Array.isArray(cotizacionDetail.meta_data)
        ? (cotizacionDetail.meta_data.find((m: any) => m.key === 'cotizacion_id')?.value ||
           cotizacionDetail.meta_data.find((m: any) => m.key === 'id')?.value)
        : '')
    );
  }

  if (!cotizacionDetail) {
    return <div className="text-center text-gray-700">Cargando...</div>;
  }

  return (
    <div className="p-10 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-start gap-4 flex-1">
          {/* Botón de retroceso mejorado */}
          <Button
            onClick={() => navigate('/cotizaciones')}
            variant="ghost"
            size="sm"
            className="mt-1"
            title="Volver a cotizaciones"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Button>
          
          <div className="flex-1">
            {/* Título con icono */}
            <div className="flex items-center gap-3 mb-2">
              <DocumentTextIcon className="h-7 w-7 text-blue-800 flex-shrink-0" />
              <h1 className="text-2xl font-semibold text-blue-800">
                Detalle de Cotización #{getNumeroCotizacion(cotizacionDetail)}
              </h1>
            </div>
            
            {/* Información adicional */}
            <div className="flex flex-col gap-1.5 ml-10">
              {/* Última actualización con icono */}
              {cotizacionDetail.date_modified && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <ClockIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <span>
                    Última actualización: <span className="font-medium">{new Date(cotizacionDetail.date_modified).toLocaleString()}</span>
                  </span>
                </div>
              )}
              
              {/* Pedido relacionado con icono */}
              {cotizacionDetail.id_pedido && (
                <div className="flex items-center gap-2 text-sm text-blue-700">
                  <LinkIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span>
                    Pedido relacionado:{' '}
                    <a
                      href={`/list/${cotizacionDetail.id_pedido}`}
                      className="font-medium underline hover:text-blue-900 transition-colors"
                    >
                      #{cotizacionDetail.id_pedido}
                    </a>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Botón de descargar PDF */}
        <div className="flex items-center gap-3 ml-4">
          <Button
            onClick={handleDownloadCotizacion}
            variant="warning"
            isLoading={isGeneratingPDF}
            disabled={isGeneratingPDF}
            leftIcon={DocumentArrowDownIcon}
            title="Descargar cotización en PDF"
          >
            {isGeneratingPDF ? 'Generando...' : 'PDF'}
          </Button>
        </div>
      </div>
 
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <CotizacionForm
        initialData={cotizacionDetail}
        mode="view"
        tiposDeDocumento={tiposDeDocumento}
        departamentos={Departamentos}
        ciudades={Ciudades}
        formaPago={formaPago}
        metodoPago={metodoPago}
      />
    </div>
  );
};

export default CotizacionDetails; 